import React, { useState, useEffect, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  VStack,
  HStack,
  Flex,
  Text,
  Image,
  Badge,
  Button,
  useColorModeValue,
  Divider,
  Input,
  useToast,
  IconButton,
  Tooltip,
  Heading,
  Icon,
  SimpleGrid,
  Progress,
  Wrap,
  Tag,
} from "@chakra-ui/react";
import { FiCreditCard, FiMic, FiShield, FiArrowRight } from "react-icons/fi";
import {
  FaHeadphones,
  FaPhotoVideo,
  FaGraduationCap,
  FaWallet,
  FaProjectDiagram,
  FaCheckSquare,
  FaTasks,
  FaSitemap,
  FaLink,
  FaFolderOpen,
  FaCalendarAlt,
  FaHandshake,
} from "react-icons/fa";
import axios from "axios";
import { hitBeApi } from "../../api/api";
import TabCacheButton from "../common/TabCacheButton";
import VoiceCreditPaymentModal from "./VoiceCreditPaymentModal";
import { useVoiceCreditPlans } from "../../hooks/useVoiceCreditPlans";
import { FaUpload } from "react-icons/fa";
import { serverBaseUrl } from "../../constants/Constants";
import { getCookieToken } from "../../utils/utils";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import {
  formatPlanCredits,
  formatPlanInr,
  getBestValuePlanName,
  getPopularPlanName,
  planCreditsPerRupee,
} from "../../utils/aiCreditPlans";
import { FEATURE_CATALOG, SITE_SUMMARY } from "../../content/productGuide";

/** Per-feature icons, keyed by the `tab` value in FEATURE_CATALOG. */
const FEATURE_ICONS = {
  "screen-listen": FaHeadphones,
  "media-analyze": FaPhotoVideo,
  "exam-ai": FaGraduationCap,
  portfolio: FaWallet,
  project: FaProjectDiagram,
  todos: FaCheckSquare,
  "daily-chore": FaTasks,
  "org-tree": FaSitemap,
  notes: FaLink,
  files: FaFolderOpen,
  "calendar-events": FaCalendarAlt,
  splitwise: FaHandshake,
};

const FEATURE_CATEGORIES = ["AI features", "Core workspace"];

function FeatureOverviewCard({ feature }) {
  const cardBg = useColorModeValue("white", "whiteAlpha.100");
  const cardBorder = useColorModeValue("gray.200", "whiteAlpha.300");
  const descColor = useColorModeValue("gray.600", "gray.400");
  const iconBg = useColorModeValue(`${feature.accent}.50`, "whiteAlpha.200");
  const IconCmp = FEATURE_ICONS[feature.tab] || FaCheckSquare;

  return (
    <Box
      as={RouterLink}
      to={`/dashboard/${feature.tab}`}
      role="group"
      display="block"
      bg={cardBg}
      p={4}
      borderRadius="xl"
      borderWidth="1px"
      borderColor={cardBorder}
      boxShadow="sm"
      transition="transform 0.2s, box-shadow 0.2s, border-color 0.2s"
      _hover={{
        transform: "translateY(-2px)",
        boxShadow: "md",
        borderColor: `${feature.accent}.300`,
      }}
    >
      <HStack align="flex-start" spacing={3}>
        <Flex
          align="center"
          justify="center"
          flexShrink={0}
          boxSize="42px"
          borderRadius="lg"
          bg={iconBg}
          color={`${feature.accent}.500`}
          sx={{ _dark: { color: `${feature.accent}.300` } }}
        >
          <Icon as={IconCmp} boxSize={5} />
        </Flex>
        <Box minW={0}>
          <HStack spacing={2} mb={1}>
            <Heading as="h4" size="sm" lineHeight="shorter">
              {feature.title}
            </Heading>
            {feature.ai ? (
              <Tag size="sm" colorScheme="teal" borderRadius="full">
                AI Credits
              </Tag>
            ) : null}
          </HStack>
          <Text fontSize="sm" color={descColor} lineHeight="tall">
            {feature.description}
          </Text>
          <HStack
            spacing={1}
            mt={2}
            color={`${feature.accent}.600`}
            sx={{ _dark: { color: `${feature.accent}.300` } }}
            fontSize="xs"
            fontWeight="semibold"
          >
            <Text>Open</Text>
            <Icon
              as={FiArrowRight}
              transition="transform 0.2s"
              _groupHover={{ transform: "translateX(3px)" }}
            />
          </HStack>
        </Box>
      </HStack>
    </Box>
  );
}

const UserProfile = () => {
  const { plans: voicePlans } = useVoiceCreditPlans(true);
  const bestValuePlanName = useMemo(
    () => getBestValuePlanName(voicePlans),
    [voicePlans]
  );
  const popularPlanName = useMemo(
    () => getPopularPlanName(voicePlans),
    [voicePlans]
  );
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);
  const [updatedUser, setUpdatedUser] = useState(user);
  const [voicePayOpen, setVoicePayOpen] = useState(false);
  const [selectedVoicePlan, setSelectedVoicePlan] = useState(null);
  const fileInputRef = useRef(null);
  const bgColor = useColorModeValue("white", "gray.800");
  const voiceCardBorder = useColorModeValue("teal.100", "teal.800");
  const voiceGlow = useColorModeValue(
    "linear(to-br, white 0%, teal.50 42%, cyan.50 100%)",
    "linear(to-br, gray.800 0%, gray.800 52%, teal.950 100%)"
  );
  const voiceInsetBg = useColorModeValue("white", "whiteAlpha.100");
  const voiceSubtitleColor = useColorModeValue("gray.600", "gray.400");
  const voiceInsetBorder = useColorModeValue("white", "whiteAlpha.200");
  const voiceMutedLabelColor = useColorModeValue("gray.500", "gray.400");
  const voiceProgressTrackBg = useColorModeValue("gray.100", "whiteAlpha.200");
  const voicePackCardBorder = useColorModeValue("teal.400", "teal.500");
  const voicePackMutedBorder = useColorModeValue("gray.200", "gray.600");
  const textColor = useColorModeValue("gray.800", "white");
  const featureSummaryColor = useColorModeValue("gray.600", "gray.400");
  const featureCategoryColor = useColorModeValue("gray.500", "gray.400");
  const toast = useToast();
  const profileDetailsFetched = useRef(false);
  const navigate = useNavigate();

  const voiceStats = useMemo(() => {
    const details = user?.userTokenDetails;
    if (!details) return { remaining: null, limit: null, used: 0 };
    const limit = Number(details.tokenLimit || 0);
    const used = Number(details.totalTokensUsed || 0);
    return {
      remaining: Math.max(0, limit - used),
      limit,
      used,
    };
  }, [user]);

  const voiceBalancePct =
    voiceStats.limit != null &&
    voiceStats.limit > 0 &&
    voiceStats.remaining !== null
      ? (voiceStats.remaining / voiceStats.limit) * 100
      : 0;

  useEffect(() => {
    const fetchUserDetails = async () => {
      if (profileDetailsFetched.current) {
        return;
      }
      profileDetailsFetched.current = true;
      try {
        const response = await hitBeApi("user", "GET");
        const userDetails = response?.data;
        setUpdatedUser(userDetails?.userDetails);
        dispatch({ type: "SET_USER", value: userDetails?.userDetails });
        toast({
          title: "User details fetched successfully!",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        profileDetailsFetched.current = false;
      } catch (error) {
        if (error.message.includes("Unauthorized")) {
          navigate("/");
          document.cookie =
            "token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
          toast({
            title: "Error",
            description: "Unauthorized: Invalid token, please login once",
            status: "error",
            duration: 2000,
            isClosable: true,
          });
          return;
        }

        toast({
          title: "Failed to fetch user details.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });

        console.error("Failed to fetch user details:", error);
        profileDetailsFetched.current = false;
      }
    };

    fetchUserDetails();
  }, [dispatch, navigate, toast]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUpdatedUser((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    try {
      const response = await hitBeApi("user", "PATCH", updatedUser);
      const savedUser = response.data;
      dispatch({ type: "SET_USER", value: savedUser });
      toast({
        title: "Profile updated successfully!",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Failed to update profile.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      console.error("Failed to update profile:", error);
    }
  };

  const handleFileUpload = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    try {
      const token = getCookieToken();
      const response = await axios.post(
        `${serverBaseUrl}/upload/aws`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            authorization: `Bearer ${token}`,
          },
        }
      );
      const fileURL = response.data.data.fileUrl;
      setUpdatedUser((prev) => ({ ...prev, profilePicture: fileURL }));
      toast({
        title: "Profile picture uploaded successfully!",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Failed to upload profile picture.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      console.error("Failed to upload profile picture:", error);
    }
  };

  const handleFileInputClick = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  return (
    <Box
      bg={bgColor}
      p={6}
      borderRadius="lg"
      boxShadow="lg"
      width={{ base: "100%", md: "80%" }}
      mx="auto"
      mt={5}
    >
      <VStack spacing={5} align="start">
        <Flex w="100%" justify="flex-end" align="center">
          <TabCacheButton tabId="profile" />
        </Flex>
        <HStack spacing={5} align="center">
          {updatedUser.profilePicture ? (
            <Image
              borderRadius="full"
              boxSize="80px"
              src={updatedUser.profilePicture}
              alt={`${updatedUser.name || "User"}'s Profile`}
              fallbackSrc="https://via.placeholder.com/80"
              boxShadow="md"
            />
          ) : (
            <Box
              borderRadius="full"
              boxSize="80px"
              bg="gray.300"
              display="flex"
              alignItems="center"
              justifyContent="center"
              fontSize="2xl"
              color={textColor}
              boxShadow="md"
            >
              ?
            </Box>
          )}
          <VStack spacing={1} align="start">
            <Tooltip label="Upload Profile Picture" placement="top">
              <IconButton
                aria-label="Upload Profile Picture"
                icon={<FaUpload />}
                onClick={handleFileInputClick}
                variant="outline"
                colorScheme="blue"
                size="sm"
                boxShadow="md"
                _hover={{ bg: "blue.600", color: "white" }}
              />
            </Tooltip>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              style={{ display: "none" }}
            />
            <Text fontSize="2xl" fontWeight="bold" color={textColor}>
              <Input
                name="name"
                value={updatedUser.name || ""}
                onChange={handleChange}
                variant="flushed"
                placeholder="Enter your name"
                focusBorderColor="blue.400"
                _placeholder={{ color: "gray.500" }}
              />
            </Text>
            <Text fontSize="md" color="gray.600">
              <Input
                name="email"
                value={updatedUser.email || ""}
                onChange={handleChange}
                variant="flushed"
                placeholder="Enter your email"
                focusBorderColor="blue.400"
                _placeholder={{ color: "gray.500" }}
              />
            </Text>
            <Text fontSize="md" color="gray.600">
              <Input
                name="phoneNumber"
                value={updatedUser.phoneNumber || ""}
                onChange={handleChange}
                variant="flushed"
                placeholder="Enter your phone number"
                focusBorderColor="blue.400"
                _placeholder={{ color: "gray.500" }}
              />
            </Text>
            <Badge
              colorScheme={updatedUser.isVerified ? "green" : "red"}
              borderRadius="full"
              px={3}
              py={1}
              fontSize="sm"
            >
              {updatedUser.isVerified ? "Verified" : "Not Verified"}
            </Badge>
          </VStack>
        </HStack>
        <Divider />
        <Box
          w="100%"
          position="relative"
          overflow="hidden"
          role="region"
          aria-label="AI Usage Credits and billing"
          borderRadius="2xl"
          borderWidth="1px"
          borderColor={voiceCardBorder}
          bgGradient={voiceGlow}
          boxShadow="0 25px 50px -12px rgba(45, 212, 191, 0.25), 0 0 0 1px rgba(45, 212, 191, 0.08)"
        >
          <Box
            pointerEvents="none"
            position="absolute"
            top="-30%"
            right="-8%"
            w="220px"
            h="220px"
            borderRadius="full"
            bg="teal.400"
            opacity={0.2}
            filter="blur(56px)"
          />
          <Box
            pointerEvents="none"
            position="absolute"
            bottom="-20%"
            left="-5%"
            w="180px"
            h="180px"
            borderRadius="full"
            bg="cyan.400"
            opacity={0.14}
            filter="blur(48px)"
          />
          <Box position="relative" zIndex={1} p={{ base: 5, md: 7 }}>
            <VStack spacing={8} align="stretch">
              <HStack align="flex-start" spacing={4} maxW="lg">
                  <Flex
                    align="center"
                    justify="center"
                    flexShrink={0}
                    boxSize={{ base: "52px", md: "56px" }}
                    borderRadius="xl"
                    bgGradient="linear(to-br, teal.400, teal.600)"
                    color="white"
                    boxShadow="lg"
                  >
                    <Icon as={FiMic} boxSize={7} aria-hidden />
                  </Flex>
                  <VStack align="start" spacing={2}>
                    <Wrap spacing={2} shouldWrapChildren>
                      <Heading
                        as="h2"
                        size="md"
                        color={textColor}
                        letterSpacing="tight"
                        lineHeight="shorter"
                      >
                        AI Usage Credits
                      </Heading>
                      <Badge colorScheme="teal" variant="solid" borderRadius="full" px={2}>
                        INR · Razorpay
                      </Badge>
                    </Wrap>
                    <HStack spacing={2} align="flex-start" color={voiceSubtitleColor}>
                      <Icon as={FiShield} boxSize={4} mt={0.5} flexShrink={0} aria-hidden />
                      <Text fontSize="sm" lineHeight="tall">
                        Secure Razorpay checkout. Use AI Usage Credits for Screen AI, Media AI,
                        portfolio voice assistant, and related features.
                      </Text>
                    </HStack>
                  </VStack>
                </HStack>

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: 4, md: 6 }}>
                <Box
                  borderRadius="xl"
                  bg={voiceInsetBg}
                  p={{ base: 5, md: 6 }}
                  boxShadow="md"
                  borderWidth="1px"
                  borderColor={voiceInsetBorder}
                  _dark={{ backdropFilter: "blur(10px)" }}
                >
                  <Text
                    fontSize="xs"
                    fontWeight="semibold"
                    textTransform="uppercase"
                    letterSpacing="widest"
                    color={voiceMutedLabelColor}
                    mb={1}
                  >
                    Balance
                  </Text>
                  <Text
                    fontSize={{ base: "4xl", md: "4xl" }}
                    fontWeight="extrabold"
                    bgGradient="linear(to-r, teal.500, teal.700)"
                    bgClip="text"
                    lineHeight="1.1"
                    letterSpacing="tight"
                  >
                    {voiceStats.remaining !== null
                      ? voiceStats.remaining.toLocaleString()
                      : "—"}
                  </Text>
                  <Text fontSize="sm" color={voiceMutedLabelColor} mt={2}>
                    AI Usage Credits available now
                  </Text>
                  {voiceStats.limit != null &&
                    voiceStats.limit > 0 &&
                    voiceStats.remaining !== null && (
                      <Box mt={5}>
                        <Flex justify="space-between" mb={1}>
                          <Text fontSize="xs" color={voiceMutedLabelColor}>
                            Pool usage
                          </Text>
                          <Text fontSize="xs" fontWeight="medium" color={textColor}>
                            {Math.round(voiceBalancePct)}% left
                          </Text>
                        </Flex>
                        <Progress
                          value={voiceBalancePct}
                          size="sm"
                          borderRadius="full"
                          colorScheme="teal"
                          bg={voiceProgressTrackBg}
                        />
                        <Text fontSize="xs" color={voiceMutedLabelColor} mt={2}>
                          {voiceStats.used.toLocaleString()} /{" "}
                          {voiceStats.limit.toLocaleString()} AI Usage Credits used
                        </Text>
                      </Box>
                    )}
                </Box>

                <Box>
                  <Text
                    fontSize="xs"
                    fontWeight="bold"
                    textTransform="uppercase"
                    letterSpacing="wider"
                    color={voiceMutedLabelColor}
                    mb={2}
                  >
                    AI Usage Credit packs
                  </Text>
                  <SimpleGrid minChildWidth="240px" spacing={3} w="full">
                    {voicePlans.map((plan) => {
                      const isPopular = plan.name === popularPlanName;
                      const isBestValue = plan.name === bestValuePlanName;
                      const hasBadge = isPopular || isBestValue;
                      const perRupee = Math.round(planCreditsPerRupee(plan));
                      return (
                        <Box
                          key={plan.name}
                          borderRadius="xl"
                          bg={voiceInsetBg}
                          p={4}
                          pt={hasBadge ? 8 : 4}
                          borderWidth="2px"
                          borderColor={
                            isPopular ? voicePackCardBorder : voicePackMutedBorder
                          }
                          boxShadow={
                            isPopular
                              ? "0 8px 30px rgba(45,212,171,0.18)"
                              : "sm"
                          }
                          position="relative"
                          overflow="hidden"
                          cursor="pointer"
                          onClick={() => {
                            setSelectedVoicePlan(plan.name);
                            setVoicePayOpen(true);
                          }}
                          _dark={{
                            backdropFilter: "blur(10px)",
                            boxShadow: isPopular
                              ? "0 8px 32px rgba(13,148,136,0.35)"
                              : "sm",
                          }}
                        >
                          {hasBadge ? (
                            <Badge
                              colorScheme={isBestValue ? "purple" : "teal"}
                              borderRadius="full"
                              px={2}
                              py={0.5}
                              position="absolute"
                              top={2}
                              right={2}
                              fontSize="0.6rem"
                              whiteSpace="nowrap"
                            >
                              {isBestValue ? "Best value" : "Popular"}
                            </Badge>
                          ) : null}
                          <Flex
                            align="flex-start"
                            justify="space-between"
                            gap={3}
                            flexWrap={{ base: "wrap", sm: "nowrap" }}
                          >
                            <Box flex="1" minW="0">
                              <Text
                                fontSize="2xs"
                                fontWeight="bold"
                                textTransform="uppercase"
                                letterSpacing="wider"
                                color={voiceMutedLabelColor}
                                noOfLines={1}
                                pr={hasBadge ? 14 : 0}
                              >
                                {plan.name}
                              </Text>
                              <Text
                                fontSize="md"
                                fontWeight="bold"
                                color={textColor}
                                mt={2}
                                lineHeight="shorter"
                              >
                                +{formatPlanCredits(plan.tokenCredits)}
                              </Text>
                              <Text
                                fontSize="xs"
                                color={voiceMutedLabelColor}
                                mt={0.5}
                              >
                                AI Usage Credits
                              </Text>
                              <Text
                                fontSize="2xs"
                                color={voiceMutedLabelColor}
                                mt={1}
                                opacity={0.9}
                              >
                                ≈{perRupee.toLocaleString("en-IN")} credits per ₹
                              </Text>
                            </Box>
                            <Box textAlign="right" flexShrink={0}>
                              <Text
                                fontSize="2xl"
                                fontWeight="extrabold"
                                color="teal.500"
                                lineHeight="1"
                                whiteSpace="nowrap"
                              >
                                {formatPlanInr(plan.amountInRupees)}
                              </Text>
                              <Text
                                fontSize="xs"
                                fontWeight="medium"
                                color={voiceMutedLabelColor}
                                mt={1}
                              >
                                one-time
                              </Text>
                            </Box>
                          </Flex>
                        </Box>
                      );
                    })}
                  </SimpleGrid>
                </Box>
              </SimpleGrid>

              <Button
                h="54px"
                px={10}
                size="lg"
                borderRadius="xl"
                alignSelf={{ base: "stretch", md: "center" }}
                maxW="md"
                mx={{ md: "auto" }}
                w={{ base: "100%", md: "auto" }}
                minW={{ md: "280px" }}
                leftIcon={<Icon as={FiCreditCard} boxSize={5} />}
                onClick={() => setVoicePayOpen(true)}
                color="white"
                boxShadow="0 12px 40px -8px rgba(20,184,166,0.55)"
                bgGradient="linear(to-r, teal.400, teal.600)"
                _hover={{
                  bgGradient: "linear(to-r, teal.500, teal.700)",
                  transform: "translateY(-2px)",
                  boxShadow: "0 18px 40px -10px rgba(20,184,166,0.6)",
                }}
                _active={{ transform: "translateY(0)" }}
                transition="all 0.22s ease"
              >
                Buy AI Usage Credits
              </Button>
            </VStack>
          </Box>
        </Box>
        <VoiceCreditPaymentModal
          isOpen={voicePayOpen}
          onClose={() => setVoicePayOpen(false)}
          variant="topUp"
          initialPlanName={selectedVoicePlan}
        />
        <Divider />
        <Box w="100%">
          <VStack align="start" spacing={1} mb={5}>
            <HStack spacing={2}>
              <Heading as="h2" size="md" color={textColor}>
                Everything you can do in Manage360
              </Heading>
              <Badge colorScheme="teal" borderRadius="full" px={2}>
                {FEATURE_CATALOG.length} tools
              </Badge>
            </HStack>
            <Text fontSize="sm" color={featureSummaryColor} lineHeight="tall">
              {SITE_SUMMARY} Everything below shares this single login—tap any
              card to open it. Tools marked{" "}
              <Tag size="sm" colorScheme="teal" borderRadius="full">
                AI Credits
              </Tag>{" "}
              can use your prepaid AI Usage Credits; the rest are always free.
            </Text>
          </VStack>

          <VStack align="stretch" spacing={6}>
            {FEATURE_CATEGORIES.map((category) => {
              const items = FEATURE_CATALOG.filter(
                (f) => f.category === category
              );
              if (items.length === 0) return null;
              return (
                <Box key={category}>
                  <Text
                    fontSize="xs"
                    fontWeight="bold"
                    textTransform="uppercase"
                    letterSpacing="wider"
                    color={featureCategoryColor}
                    mb={3}
                  >
                    {category}
                  </Text>
                  <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={4}>
                    {items.map((feature) => (
                      <FeatureOverviewCard key={feature.tab} feature={feature} />
                    ))}
                  </SimpleGrid>
                </Box>
              );
            })}
          </VStack>
        </Box>
        <Divider />
        <Button
          colorScheme="blue"
          onClick={handleSave}
          width="full"
          boxShadow="lg"
          _hover={{ bg: "blue.600", transform: "scale(1.05)" }}
          transition="transform 0.2s"
        >
          Save Profile
        </Button>
      </VStack>
    </Box>
  );
};

export default UserProfile;
